(() => {
  if (window.TasteRecommendations) return;
  const grids = new Map();
  const noteCache = new WeakMap();
  let cartContext = null;
  let syncFrame = 0;
  let refreshTimer = 0;
  let requestVersion = 0;
  let activeRequest;
  let dialog;
  let opener;
  let outsideDown = false;

  const normalize = value => String(value || '').trim().toLowerCase();

  function parseContext(text) {
    const value = JSON.parse(text);
    if (typeof value.hasPerfume !== 'boolean' || !Array.isArray(value.scents) ||
        value.scents.some(note => typeof note !== 'string')) {
      throw new Error('Invalid taste context');
    }
    return { hasPerfume: value.hasPerfume, scents: new Set(value.scents.map(normalize).filter(Boolean)) };
  }

  function productNotes(card) {
    const raw = card.getAttribute('data-taste-scents') || '[]';
    const cached = noteCache.get(card);
    if (cached?.raw === raw) return cached.notes;
    try {
      const data = JSON.parse(raw);
      const notes = Array.isArray(data) ? data.map(normalize).filter(Boolean) : [];
      noteCache.set(card, { raw, notes });
      return notes;
    } catch (error) {
      return [];
    }
  }

  function setAttribute(element, name, value) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  }

  function applyMatches() {
    if (!cartContext) return;
    document.querySelectorAll('[data-taste-product]').forEach(card => {
      const matched = cartContext.hasPerfume &&
        productNotes(card).filter(note => cartContext.scents.has(note)).length >= 2;
      const slot = card.querySelector(':scope > [data-taste-slot]');
      if (!slot) return;
      const value = matched ? 'true' : 'false';
      const changed = slot.dataset.tasteMatch !== value;
      setAttribute(card, 'data-taste-match', value);
      setAttribute(slot, 'data-taste-match', value);
      if (matched) {
        slot.hidden = false;
        slot.removeAttribute('aria-hidden');
        slot.removeAttribute('data-taste-spacer');
      } else if (changed) {
        slot.hidden = true;
        setAttribute(slot, 'aria-hidden', 'true');
        slot.removeAttribute('data-taste-spacer');
      }
      const wrapper = card.closest('[data-pagination-key]');
      if (wrapper?.parentElement?.matches('[data-taste-grid]')) {
        wrapper.classList.toggle('smart-ranking-first', matched);
      }
    });
  }

  class RowAlignment {
    constructor(grid) {
      this.grid = grid;
      this.frame = 0;
      this.observed = new Set();
      this.schedule = this.schedule.bind(this);
      this.resize = 'ResizeObserver' in window ? new ResizeObserver(this.schedule) : null;
      this.resize?.observe(grid);
      this.mutations = new MutationObserver(records => {
        if (records.some(record => record.type === 'childList' || record.target === grid ||
            record.target === grid.parentElement || record.target.parentElement === grid)) {
          this.schedule();
        }
      });
      this.mutations.observe(grid, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      if (grid.parentElement) this.mutations.observe(grid.parentElement, { attributes: true, attributeFilter: ['class'] });
      window.addEventListener('resize', this.schedule, { passive: true });
      this.schedule();
    }

    schedule() {
      if (!this.frame) this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (this.grid.isConnected) this.align();
      });
    }

    align() {
      const children = new Set(this.grid.children);
      for (const element of this.observed) {
        if (!children.has(element)) {
          this.resize?.unobserve(element);
          this.observed.delete(element);
        }
      }
      for (const element of children) {
        if (!this.observed.has(element)) {
          this.observed.add(element);
          this.resize?.observe(element);
        }
      }
      const cards = [];
      for (const element of children) {
        if (!element.matches('.pr-grid-item-collection')) continue;
        const slot = element.querySelector('[data-taste-slot]');
        if (!slot) continue;
        const rect = element.getBoundingClientRect();
        if (!rect.width || !rect.height) continue;
        const matched = slot.dataset.tasteMatch === 'true';
        cards.push({ top: rect.top, slot, matched, height: matched ? slot.getBoundingClientRect().height : 0 });
      }
      cards.sort((a, b) => a.top - b.top);
      const rows = [];
      for (const card of cards) {
        let row = rows[rows.length - 1];
        if (!row || Math.abs(row.top - card.top) > 1) {
          row = { top: card.top, cards: [], height: 0 };
          rows.push(row);
        }
        row.cards.push(card);
        if (card.matched) row.height = Math.max(row.height, card.height || 33);
      }
      for (const row of rows) {
        for (const card of row.cards) {
          const spacer = !card.matched && row.height > 0;
          card.slot.hidden = !card.matched && !spacer;
          if (spacer) {
            setAttribute(card.slot, 'data-taste-spacer', '');
            setAttribute(card.slot, 'aria-hidden', 'true');
            const height = row.height + 'px';
            if (card.slot.style.getPropertyValue('--taste-row-height') !== height) {
              card.slot.style.setProperty('--taste-row-height', height);
            }
          } else {
            card.slot.removeAttribute('data-taste-spacer');
            card.slot.style.removeProperty('--taste-row-height');
            if (card.matched) card.slot.removeAttribute('aria-hidden');
          }
        }
      }
    }

    destroy() {
      cancelAnimationFrame(this.frame);
      this.resize?.disconnect();
      this.mutations.disconnect();
      window.removeEventListener('resize', this.schedule);
    }
  }

  function synchronize() {
    syncFrame = 0;
    if (!cartContext) {
      const initial = document.querySelector('[data-taste-cart-context]');
      if (initial) {
        try { cartContext = parseContext(initial.textContent); }
        catch (error) { console.warn('[taste]', error); }
      }
    }
    applyMatches();
    for (const [grid, controller] of grids) {
      if (!grid.isConnected) {
        controller.destroy();
        grids.delete(grid);
      }
    }
    document.querySelectorAll('[data-taste-grid]').forEach(grid => {
      if (!grids.has(grid)) grids.set(grid, new RowAlignment(grid));
      const controller = grids.get(grid);
      cancelAnimationFrame(controller.frame);
      controller.frame = 0;
      controller.align();
    });
  }

  function scheduleSync() {
    if (!syncFrame) syncFrame = requestAnimationFrame(synchronize);
  }

  async function refreshCart() {
    const version = ++requestVersion;
    activeRequest?.abort();
    const controller = new AbortController();
    activeRequest = controller;
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', 'taste-cart-context');
      const response = await fetch(url.href, {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error('Taste context HTTP ' + response.status);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const context = html.querySelector('[data-taste-cart-context]');
      if (!context) throw new Error('Taste context missing');
      const next = parseContext(context.textContent);
      if (version !== requestVersion) return false;
      cartContext = next;
      scheduleSync();
      return true;
    } catch (error) {
      if (version === requestVersion) console.warn('[taste] Cart update not applied:', error);
      return false;
    } finally {
      clearTimeout(timeout);
      if (activeRequest === controller) activeRequest = null;
    }
  }

  function queueRefresh() {
    requestVersion++;
    activeRequest?.abort();
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCart, 120);
  }

  function ensureDialog() {
    if (dialog?.isConnected) return dialog;
    dialog = document.createElement('dialog');
    dialog.setAttribute('data-taste-dialog', '');
    dialog.id = 'taste-explanation-dialog';
    dialog.setAttribute('aria-labelledby', 'taste-explanation-title');
    dialog.setAttribute('aria-describedby', 'taste-explanation-description');
    dialog.innerHTML = `
      <span class="taste-dialog-handle" aria-hidden="true"></span>
      <button type="button" class="taste-dialog-close" data-taste-close aria-label="Erklärung schließen">×</button>
      <span class="taste-dialog-eyebrow">Aus deinem Warenkorb</span>
      <h2 id="taste-explanation-title">Das könnte dein nächster Lieblingsduft sein.</h2>
      <p class="taste-dialog-description" id="taste-explanation-description">Mindestens zwei Duftnoten dieses Produkts stimmen mit Duftnoten der Parfums in deinem Warenkorb überein. Deshalb markieren wir es mit „Dein Geschmack“.</p>
      <p class="taste-dialog-note">Wir orientieren uns an deiner aktuellen Auswahl. Ein ähnliches Duftprofil ist eine persönliche Empfehlung – keine Garantie, dass dir der Duft gefällt.</p>
      <button type="button" class="taste-dialog-confirm" data-taste-close autofocus>Verstanden</button>`;
    dialog.addEventListener('click', event => {
      if (event.target.closest('[data-taste-close]')) dialog.close();
      else if (outsideDown && isOutside(event)) dialog.close();
      outsideDown = false;
    });
    dialog.addEventListener('pointerdown', event => { outsideDown = isOutside(event); });
    dialog.addEventListener('close', () => {
      if (opener?.isConnected && opener.getClientRects().length) opener.focus({ preventScroll: true });
      opener = null;
    });
    document.body.appendChild(dialog);
    return dialog;
  }

  function isOutside(event) {
    if (event.target !== dialog) return false;
    const rect = dialog.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right ||
      event.clientY < rect.top || event.clientY > rect.bottom;
  }

  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-taste-info]');
    if (!button) return;
    const slot = button.closest('[data-taste-slot]');
    if (!slot || slot.hidden || slot.dataset.tasteMatch !== 'true') return;
    event.preventDefault();
    event.stopPropagation();
    opener = button;
    const modal = ensureDialog();
    button.setAttribute('aria-controls', modal.id);
    if (typeof modal.showModal === 'function') {
      if (!modal.open) modal.showModal();
    } else {
      window.alert(modal.querySelector('.taste-dialog-description').textContent);
    }
  });

  document.addEventListener('eurus:cart:items-changed', queueRefresh);
  window.addEventListener('eurus:cart:items-changed', queueRefresh);
  document.addEventListener('taste:cart-updated', queueRefresh);
  window.addEventListener('taste:cart-updated', queueRefresh);
  window.addEventListener('pageshow', event => { if (event.persisted) queueRefresh(); });
  window.TasteRecommendations = { refresh: refreshCart, align: scheduleSync };

  function start() {
    synchronize();
    const changes = new MutationObserver(records => {
      const relevant = records.some(record =>
        (record.removedNodes.length && Array.from(grids.keys()).some(grid => !grid.isConnected)) ||
        Array.from(record.addedNodes).some(node => node.nodeType === 1 &&
          (node.matches('[data-taste-grid], [data-taste-product], [data-taste-cart-context]') ||
           node.querySelector('[data-taste-grid], [data-taste-product], [data-taste-cart-context]')))
      );
      if (relevant) scheduleSync();
    });
    changes.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
